<main class="page">
  <div class="steps">
    @for (step of steps(); track step.number) {
      <button type="button" class="step" [class.active]="step.active" [class.completed]="step.completed" [disabled]="!isStepClickable(step)" (click)="goToStep(step)">
        <span class="step-number">{{ step.number }}</span>
        <span class="step-label">{{ step.label }}</span>
      </button>
    }
  </div>

  <h1 class="page-title">3. Questionnaire</h1>

  @if (apiError()) {
    <div class="alert alert-warning" role="alert">{{ apiError() }}</div>
  }

  @if (saveMessage()) {
    <div class="alert alert-info" role="status">{{ saveMessage() }}</div>
  }

  <div class="summary-card">
    
    <span class="summary-pill">{{ draft().projectManagementType || '-' }}</span>
    <span class="summary-item">Estimate Title: <strong>{{ draft().estimateTitle || '-' }}</strong></span>
    <span class="summary-item">Project Name: <strong>{{ draft().projectTitle || '-' }}</strong></span>
    <span class="summary-item">AFE: <strong>{{ draft().afeNumber || '-' }}</strong></span>
    <span class="summary-item summary-item--progress">
      Design Progress:
      <span class="progress-badge">{{ formattedDesignProgress() }}</span>
    </span>
    <span class="summary-item">Class of Estimate: <strong>{{ classNumber() || '-' }}</strong></span>
  </div>

  <p class="subtitle">Definitive estimate. Confirm firm quantities, quotes and schedule alignment.</p>

  @if (isLoading()) {
    <p class="subtitle">Loading questionnaire from backend...</p>
  } @else {
    <div class="question-list">
      @for (group of groupedQuestions(); track group.groupName || 'ungrouped') {
        @if (group.groupName) {
          <h2 class="group-title">{{ group.groupName }}</h2>
        }
        @for (question of group.questions; track question.id) {
          <section class="question-card">
            <div class="question-title-row">
              <span class="question-id">{{ formatQuestionNumber(question.displayNumber) }}</span>
              <h3 class="question-title">
                {{ question.text }}
                @if (question.isMandatory) {
                  <span class="required" aria-label="Required">*</span>
                }
              </h3>
            </div>

            <div class="comment-row">
              <label class="comment-label" [attr.for]="'comment-' + question.displayNumber">Comments/Links:</label>
              @if (isReadonlyQuestionnaire()) {
                <div class="comment-readonly" [class.is-invalid]="isCommentMissing(question)">
                  @for (segment of getCommentSegments(question.comment); track $index) {
                    @if (segment.href) {
                      <a [href]="segment.href" target="_blank" rel="noopener noreferrer">{{ segment.text }}</a>
                    } @else {
                      <span>{{ segment.text }}</span>
                    }
                  }
                </div>
              } @else {
                <input
                  class="comment-input"
                  [class.is-invalid]="isCommentMissing(question)"
                  [id]="'comment-' + question.displayNumber"
                  type="text"
                  [ngModel]="question.comment"
                  (ngModelChange)="onCommentChange(question.id, $event)"
                />
              }

              @if (!isReadonlyQuestionnaire() && hasCommentLinks(question.comment)) {
                <div class="comment-link-preview">
                  @for (segment of getCommentSegments(question.comment); track $index) {
                    @if (segment.href) {
                      <a [href]="segment.href" target="_blank" rel="noopener noreferrer">{{ segment.text }}</a>
                    }
                  }
                </div>
              }
              @if (isCommentMissing(question)) {
                <p class="field-error">Comment/Link is required for this question.</p>
              }

              @if (question.hasAttachment) {
                <div class="attachment-actions" [class.attachment-actions--invalid]="isAttachmentMissing(question)">
                  <label
                    class="file-btn"
                    [class.has-file]="question.fileName"
                    [attr.for]="'file-' + question.displayNumber"
                    [attr.id]="'attachment-trigger-' + question.displayNumber"
                    tabindex="-1"
                  >
                    <mat-icon class="file-icon">attach_file</mat-icon>
                    {{ question.fileName || (question.isMandatoryAttachment ? 'Attach File *' : 'Attach File') }}
                  </label>
                  <input
                    class="file-input"
                    [id]="'file-' + question.displayNumber"
                    type="file"
                    (change)="onFileSelected(question.id, $event)"
                    aria-label="Upload attachment"
                    [disabled]="isReadonlyQuestionnaire()"
                  />
                  @if (question.fileName || question.attachmentPath) {
                    <button class="btn btn-sm btn-link-danger" type="button" (click)="removeAttachment(question.id, question.displayNumber)" [disabled]="isReadonlyQuestionnaire()">
                      <mat-icon>close</mat-icon> Remove
                    </button>
                  }
                </div>
                @if (isAttachmentMissing(question)) {
                  <p class="field-error">Attachment is required for this question.</p>
                }
              }
            </div>

            @if (question.attachmentPath) {
              <div class="attachment-info">
                <span class="attachment-name">{{ question.fileName || question.attachmentPath }}</span>
                <button class="btn btn-sm btn-secondary" type="button" (click)="downloadFile(question.attachmentPath, question.fileName)">
                  <mat-icon>download</mat-icon> Download
                </button>
              </div>
            }
          </section>
        }
      }
    </div>
  }

  <div class="actions">
    <button class="btn btn-secondary" type="button" (click)="goBack()">‹ Back to Project Details</button>
    <div class="actions-right">
      @if (!isReadonlyQuestionnaire() && shouldShowSaveDraft()) {
        <button class="btn btn-outline" type="button" [disabled]="isSaving()" (click)="saveDraft()">Save as Draft</button>
      }
      @if (!isReadonlyQuestionnaire() && shouldShowSave()) {
        <button class="btn btn-outline" type="button" [disabled]="isSaving()" (click)="save()">Save</button>
      }
      @if (!isReadonlyQuestionnaire() && shouldShowSubmitRequest()) {
        <button class="btn btn-primary" type="button" [disabled]="isSaving()" (click)="submitRequest()">Submit CE Request</button>
      }
      @if (shouldShowGoToResponseButton()) {
        <button class="btn btn-primary" type="button" [disabled]="isSaving()" (click)="goToResponsePage()">Go To Response Page</button>
      }
    </div>
  </div>
</main>
